// Power-ups: the ten plants that appear on the surface, and their effects.
// Ports `PowerUp.cs` (four state constants and nothing else — a power-up is a
// `CubeWalker` that never walks) plus the power-up half of `BugsModel.cs`:
// `CheckForPowerUpCreation`/`AddPowerUp2` (spawning), `TakePowerUp` (the ten
// effects), `HandleOccupationOnEnteredBlock` (bugs trample them) and
// `RepairBlocksAround`/`GetSpiral`/`RepairCube` from `BlocksModel.cs`.
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).

import type { EventBus } from "./events";
import { pickWeightedIndex, type Rng } from "./rng";
import type { BlocksModel, Cell } from "./blocks";
// Type-only import: erased at build time, so the bugs <-> powerups cycle never
// exists at runtime (bugs.ts only sees the `PowerUpSink` interface it defines).
import type { BugManager } from "./bugs";
import type { LevelSource } from "./levels";
import type { GlobalModifiers } from "./modifiers";
import { poseToCell, poseToWorld, DIR_UV, FACES, N, type FaceId, type SurfacePose, type Vec3 } from "./surface";
import {
  FIRST_POWERUP_SECONDS,
  POWERUP_DEFS,
  POWERUP_DIE_SECONDS,
  POWERUP_FREEZE_SECONDS,
  POWERUP_GROW_SECONDS,
  POWERUP_PICK_SECONDS,
  POWERUP_SPAWN_RATE,
  POWERUP_SPEED_FAST,
  POWERUP_SPEED_SLOW,
  POWERUP_TIMED_SECONDS,
  REPAIR_SEVEN,
  REPAIR_THREE,
  type PowerUpEffect,
} from "./defs";

/**
 * Branded entity id (00-architecture.md §6). Counts **down from -1000**, below
 * `SpawnerManager`'s -1, so the three id spaces stay disjoint on sight even
 * though power-ups never enter `Occupancy`.
 */
export type PowerUpId = number & { readonly __brand: "PowerUpId" };

/**
 * Lifecycle as a discriminated union (00-architecture.md §5). `progress` is
 * normalised to [0, 1] over the state's duration, exactly like `BugState`'s.
 */
export type PowerUpState =
  | { kind: "growing"; progress: number } // grow-in; already collectable
  | { kind: "idle" }
  | { kind: "taken"; progress: number } // tapped: `pick` clip, then removed
  | { kind: "killed"; progress: number }; // a bug walked over it: `die` clip

export interface PowerUp {
  readonly id: PowerUpId;
  /** 0..9, indexes `POWERUP_DEFS` — behaviour keys off this, never the effect
   * enum ordinal (the slot 8/9 swap, plan 16 §2). */
  readonly slot: number;
  /** Static; `dir` only orients the model (the original's fixed `(1, 0)`). */
  readonly pose: SurfacePose;
  /**
   * The block it stands on. Placement marches inward past `empty`, so this can
   * sit **deeper** than `poseToCell(pose)` — a power-up on a crater floor.
   */
  readonly cell: Cell;
  state: PowerUpState;
}

/** The states a tap can collect — `TakePowerUp` accepts `Idle || Growing`. */
export function isCollectable(state: PowerUpState): boolean {
  return state.kind === "growing" || state.kind === "idle";
}

/** Same guard as bug states: 60 x 1/60 sums to a hair under 1. */
const PROGRESS_EPSILON = 1e-9;

/** Linear index of a grid cell, for the manager's cell index. */
function cellKey(cell: Cell): number {
  return (cell[2] * N + cell[1]) * N + cell[0];
}

/** The axis a face's normal runs along: faces 0,1 → x; 2,3 → y; 4,5 → z. */
function faceAxis(face: FaceId): 0 | 1 | 2 {
  return Math.floor(face / 2) as 0 | 1 | 2;
}

/**
 * The Ulam spiral `BlocksModel.GetSpiral` walks for the N-block repairs:
 * starts on the centre itself, then steps in runs of 1, 1, 2, 2, 3, 3, …
 * through the four directions, yielding only in-bounds coordinates.
 */
export function* spiral(a: number, b: number): Generator<readonly [number, number]> {
  const dirs = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ] as const;
  let direction = 0;
  let walked = 0;
  let run = 1;
  let turns = 0;
  for (let i = 0; i < N * N * 4; i++) {
    if (a >= 0 && b >= 0 && a < N && b < N) yield [a, b];
    a += dirs[direction][0];
    b += dirs[direction][1];
    walked++;
    if (walked === run) {
      walked = 0;
      turns++;
      direction = (direction + 1) % 4;
      if (turns === 2) {
        turns = 0;
        run++;
      }
    }
  }
}

/**
 * World placement of a power-up, for the renderer. A surface power-up gets the
 * rounded-box frame every walker uses; a **recessed** one (placed through a
 * crater) sits on the flat floor of the hole, where the rounding does not
 * apply — its frame is the cell's outward face taken sharp.
 */
export function powerUpFrame(p: PowerUp): { pos: Vec3; normal: Vec3; forward: Vec3 } {
  const surface = poseToCell(p.pose);
  if (surface.x === p.cell[0] && surface.y === p.cell[1] && surface.z === p.cell[2]) {
    return poseToWorld(p.pose);
  }
  const f = FACES[p.pose.face];
  const cellSize = 1 / N;
  const centre = (c: number) => -0.5 + (c + 0.5) * cellSize;
  const half = cellSize / 2;
  const pos: Vec3 = [
    centre(p.cell[0]) + f.normal[0] * half,
    centre(p.cell[1]) + f.normal[1] * half,
    centre(p.cell[2]) + f.normal[2] * half,
  ];
  const [du, dv] = DIR_UV[p.pose.dir];
  const forward: Vec3 = [
    f.uAxis[0] * du + f.vAxis[0] * dv,
    f.uAxis[1] * du + f.vAxis[1] * dv,
    f.uAxis[2] * du + f.vAxis[2] * dv,
  ];
  return { pos, normal: f.normal, forward };
}

/**
 * Owns every live power-up, the spawn timer, and the countdowns of the three
 * timed effects. Keeps its own **cell index** rather than entering `Occupancy`:
 * power-ups must be invisible to walker collision (bugs walk straight over
 * them) and visible to the repair gate — the exact opposite split from what a
 * shared claim space gives (`FindBlockOccupant` excludes power-ups while
 * `GetCubeWalkerBlockingPlaneRepair` includes them, plan 16 §2). The two
 * consumers ask explicitly: `applyCommand`'s repair gate calls `at(cell)`,
 * `updateBug`'s tile-entry hook calls `killAt(cell)`.
 */
export class PowerUpManager {
  private readonly map = new Map<PowerUpId, PowerUp>();
  /** The power-up standing on a cell. At most one per cell; only `growing` and
   * `idle` ones are indexed — taken and killed ones stop counting at once. */
  private readonly byCell = new Map<number, PowerUpId>();
  private nextId = -1000;
  /** Seconds until the next placement attempt; stays negative across failed
   * attempts — that non-reset is the game's real spawn throttle (plan 16 §2). */
  private nextIn = FIRST_POWERUP_SECONDS;
  // Countdowns of the timed effects; each collection restarts the full window.
  private speedLeft = 0;
  private spawnLeft = 0;
  private freezeLeft = 0;

  constructor(
    private readonly events: EventBus,
    private readonly rng: Rng,
    private readonly blocks: BlocksModel,
    // Needed for the kill-half effect and (via its occupancy) the placement
    // and repair gates; the reverse link is the narrow `PowerUpSink` in bugs.ts.
    private readonly bugs: BugManager,
    private readonly levels: LevelSource,
    private readonly modifiers: GlobalModifiers,
  ) {}

  get powerups(): ReadonlyMap<PowerUpId, PowerUp> {
    return this.map;
  }

  /** The collectable power-up standing on a cell, or null. */
  at(cell: Cell): PowerUp | null {
    const id = this.byCell.get(cellKey(cell));
    return id === undefined ? null : (this.map.get(id) ?? null);
  }

  /**
   * Place a power-up through face cell `(u, v)` (integers), marching inward
   * past `empty` blocks; the first non-empty block must be `healthy` and
   * unoccupied or nothing is placed — `AddPowerUp2` (`BugsModel.cs:1840-1865`).
   * Public so tests and the debug hook can place deterministically; the spawn
   * timer path draws its arguments from the RNG and calls this.
   */
  place(slot: number, face: FaceId, u: number, v: number): PowerUp | null {
    const cell = this.marchInward(face, u, v);
    if (cell === null) return null;
    const pose: SurfacePose = { face, u: u + 0.5, v: v + 0.5, dir: 0 };
    const powerUp: PowerUp = { id: this.nextId-- as PowerUpId, slot, pose, cell, state: { kind: "growing", progress: 0 } };
    this.map.set(powerUp.id, powerUp);
    this.byCell.set(cellKey(cell), powerUp.id);
    this.events.emit("powerUpSpawned", { id: powerUp.id, slot });
    return powerUp;
  }

  /**
   * A tap — `TakePowerUp` (`BugsModel.cs:2919-2990`). Collectable while still
   * growing; awards the slot's points unmultiplied and **before** the effect
   * (`Cube.cs:1166-1169` keeps power-up points out of the combo multiplier),
   * then applies the effect **while the power-up is still in the cell index**,
   * so a repair effect vetoes the repair of its own block, and only then drops
   * it from the index. Anything else — already taken, killed, unknown — is a
   * no-op, which is what makes a second tap during the `pick` clip safe.
   */
  take(id: PowerUpId): void {
    const powerUp = this.map.get(id);
    if (!powerUp || !isCollectable(powerUp.state)) return;
    powerUp.state = { kind: "taken", progress: 0 };
    this.events.emit("powerUpStateChanged", { id, state: "taken" });
    const def = POWERUP_DEFS[powerUp.slot];
    this.events.emit("scored", { points: def.points, multiplier: 1 });
    this.events.emit("powerUpTaken", { id, slot: powerUp.slot, points: def.points });
    this.applyEffect(def.effect, powerUp);
    this.byCell.delete(cellKey(powerUp.cell));
  }

  /**
   * A walker entered this cell — `HandleOccupationOnEnteredBlock`
   * (`BugsModel.cs:1752-1760`): the power-up is destroyed, plays its `die`
   * clip, and awards nothing.
   */
  killAt(cell: Cell): void {
    const key = cellKey(cell);
    const id = this.byCell.get(key);
    if (id === undefined) return;
    const powerUp = this.map.get(id);
    if (!powerUp) return;
    powerUp.state = { kind: "killed", progress: 0 };
    this.byCell.delete(key);
    this.events.emit("powerUpStateChanged", { id, state: "killed" });
  }

  /** Advance timers, lifecycle states, and the spawn attempt by `dt` seconds. */
  update(dt: number): void {
    this.tickModifiers(dt);
    for (const powerUp of this.map.values()) {
      const result = this.step(powerUp, dt);
      if (result === "changed") {
        this.events.emit("powerUpStateChanged", { id: powerUp.id, state: powerUp.state.kind });
      } else if (result === "removed") {
        this.map.delete(powerUp.id);
        this.events.emit("powerUpRemoved", { id: powerUp.id });
      }
    }
    this.nextIn -= dt;
    if (this.nextIn < 0) this.tryCreate();
  }

  /** The three effect countdowns — `CheckForBugsFrozen`, `CheckForGlobalSpeed
   * Timer`, `CheckForSpawnRateTimer` (`BugsModel.cs:791-830`). */
  private tickModifiers(dt: number): void {
    if (this.speedLeft > 0) {
      this.speedLeft -= dt;
      if (this.speedLeft <= 0) this.modifiers.speedFactor = 1;
    }
    if (this.spawnLeft > 0) {
      this.spawnLeft -= dt;
      if (this.spawnLeft <= 0) this.modifiers.spawnRate = 1;
    }
    if (this.freezeLeft > 0) {
      this.freezeLeft -= dt;
      if (this.freezeLeft <= 0) this.modifiers.frozen = false;
    }
  }

  /**
   * The one place power-up transitions live (00-architecture.md §5). The
   * original drives these off `Animation.isPlaying` (`UpdatePowerUps`,
   * `BugsModel.cs:832-859`); the port has no such signal, so the cooked clip
   * durations time them, exactly as `DIE_SECONDS` does for bugs.
   */
  private step(powerUp: PowerUp, dt: number): "none" | "changed" | "removed" {
    const state = powerUp.state;
    switch (state.kind) {
      case "growing":
        state.progress += dt / POWERUP_GROW_SECONDS;
        if (state.progress < 1 - PROGRESS_EPSILON) return "none";
        powerUp.state = { kind: "idle" };
        return "changed";
      case "idle":
        return "none";
      case "taken":
        state.progress += dt / POWERUP_PICK_SECONDS;
        return state.progress < 1 - PROGRESS_EPSILON ? "none" : "removed";
      case "killed":
        state.progress += dt / POWERUP_DIE_SECONDS;
        return state.progress < 1 - PROGRESS_EPSILON ? "none" : "removed";
      default: {
        const unreachable: never = state;
        throw new Error(`unhandled power-up state ${JSON.stringify(unreachable)}`);
      }
    }
  }

  /**
   * One placement attempt — `CheckForPowerUpCreation`: a uniform face and a
   * uniform cell on it. Only success resets the timer (`SetPowerUpCreationTime`
   * is on the success path alone), so a failure retries **next step** — the
   * original re-rolls every frame, and this attempt-per-step reproduction is
   * what keeps a damaged cube spawning fewer power-ups. It also couples every
   * other system's RNG stream to how damaged the cube is; that coupling is the
   * original's own (see `DIVERGENCES.md`).
   */
  private tryCreate(): void {
    const face = Math.floor(this.rng() * 6) as FaceId;
    const u = Math.floor(this.rng() * N);
    const v = Math.floor(this.rng() * N);
    if (this.marchInward(face, u, v) === null) return;
    const slot = pickWeightedIndex(this.rng, this.levels.current.powerUpWeights);
    // Unreachable with the shipped tables (every row sums to 100, asserted in
    // tests); the original resets the timer on this branch, so this does too.
    if (slot === null) {
      this.resetTimer();
      return;
    }
    this.place(slot, face, u, v);
    this.resetTimer();
  }

  /** `mean ± 10%`, uniform (`SetPowerUpCreationTime`, `BugsModel.cs:1833-1838`). */
  private resetTimer(): void {
    const mean = this.levels.current.meanTimeBetweenPowerUpCreation;
    const tenPercent = mean * 0.1;
    this.nextIn = mean - tenPercent + this.rng() * tenPercent * 2;
  }

  /**
   * The ray-march inward from face cell `(u, v)`: skip `empty` blocks; the
   * first non-empty one is the candidate, accepted only if `healthy` with no
   * walker claim and no power-up on it. Returns the cell, or null.
   */
  private marchInward(face: FaceId, u: number, v: number): Cell | null {
    const outer = poseToCell({ face, u: u + 0.5, v: v + 0.5, dir: 0 });
    const normal = FACES[face].normal;
    for (let depth = 0; depth < N; depth++) {
      const cell: Cell = [
        outer.x - normal[0] * depth,
        outer.y - normal[1] * depth,
        outer.z - normal[2] * depth,
      ];
      const state = this.blocks.state(cell[0], cell[1], cell[2]);
      if (state === "empty") continue;
      if (state !== "healthy") return null;
      if (this.bugs.occupancy.holdersByCell(cell).length > 0) return null;
      if (this.byCell.has(cellKey(cell))) return null;
      return cell;
    }
    return null; // the whole column is gone
  }

  /**
   * The ten effects — the switch in `TakePowerUp` (`BugsModel.cs:2933-2985`).
   * The point-bonus slots are deliberately empty: every power-up's points are
   * already awarded before the switch, and "point bonus" simply means "a
   * power-up with no side effect".
   */
  private applyEffect(effect: PowerUpEffect, powerUp: PowerUp): void {
    switch (effect) {
      case "killHalf": {
        // "50% bugs die" is neither 50% nor a kill: every **even list index**
        // is hurt (deterministic), black bugs are skipped (tapping one would
        // be game over), and `hurt` means the fat bug takes one of its three.
        let index = 0;
        for (const bug of [...this.bugs.bugs.values()]) {
          if (bug.kindDef !== "black" && index % 2 === 0) this.bugs.hurt(bug.id);
          index++;
        }
        return;
      }
      case "doubleSpawn":
        this.modifiers.spawnRate = POWERUP_SPAWN_RATE;
        this.spawnLeft = POWERUP_TIMED_SECONDS;
        return;
      case "halfSpeed":
        // Assign, never multiply: the two speed effects are mutually exclusive
        // and each collection restarts the full window (`BugsModel.cs:2953-2963`).
        this.modifiers.speedFactor = POWERUP_SPEED_SLOW;
        this.speedLeft = POWERUP_TIMED_SECONDS;
        return;
      case "doubleSpeed":
        this.modifiers.speedFactor = POWERUP_SPEED_FAST;
        this.speedLeft = POWERUP_TIMED_SECONDS;
        return;
      case "freeze":
        this.modifiers.frozen = true;
        this.freezeLeft = POWERUP_FREEZE_SECONDS;
        return;
      case "repairAll":
        // `RepairCube` routes through the full `RepairBlock`, so every cell
        // goes through the blocker gate: a walker on a hole vetoes part of the
        // "complete" repair, and this power-up vetoes its own block.
        for (let z = 0; z < N; z++) {
          for (let y = 0; y < N; y++) {
            for (let x = 0; x < N; x++) {
              if (this.blocks.state(x, y, z) !== "healthy") this.tryRepair([x, y, z]);
            }
          }
        }
        return;
      case "repairSeven":
        this.repairAround(powerUp, REPAIR_SEVEN);
        return;
      case "repairThree":
        this.repairAround(powerUp, REPAIR_THREE);
        return;
      case "points":
        return;
      default: {
        const unreachable: never = effect;
        throw new Error(`unhandled power-up effect ${JSON.stringify(unreachable)}`);
      }
    }
  }

  /**
   * The N-block spiral repair — `RepairAroundPowerUp` + `RepairBlocksAround`
   * (`BlocksModel.cs:286-330`). The spiral runs over the two coordinates other
   * than the face's normal axis, at the power-up's **own** depth (a recessed
   * power-up repairs its recessed layer for free). Candidates are `ghost` and
   * `empty` only — infected blocks are not healed by this effect, unlike
   * `repairAll` — and only *successful* repairs count toward `amount`, so a
   * veto does not use up a slot and the spiral may manage fewer than N.
   */
  private repairAround(powerUp: PowerUp, amount: number): void {
    const axis = faceAxis(powerUp.pose.face);
    const [cx, cy, cz] = powerUp.cell;
    const start: readonly [number, number] =
      axis === 0 ? [cy, cz] : axis === 1 ? [cx, cz] : [cx, cy];
    let left = amount;
    for (const [a, b] of spiral(start[0], start[1])) {
      const cell: Cell = axis === 0 ? [cx, a, b] : axis === 1 ? [a, cy, b] : [a, b, cz];
      const state = this.blocks.state(cell[0], cell[1], cell[2]);
      if (state !== "ghost" && state !== "empty") continue;
      if (this.tryRepair(cell)) {
        left--;
        if (left === 0) return;
      }
    }
  }

  /**
   * A repair through the same blocker gate a player repair uses — walkers
   * whose claims cover the cell, plus any power-up standing on it (this is
   * where a repair power-up vetoes its own block). Effect repairs do not feed
   * the combo chain and, unlike the player path, do not announce their
   * blockers — the original calls `RepairBlock(x, y, z, false)`.
   */
  private tryRepair(cell: Cell): boolean {
    if (this.bugs.occupancy.holdersByCell(cell).length > 0) return false;
    if (this.at(cell) !== null) return false;
    return this.blocks.repair(cell[0], cell[1], cell[2]);
  }
}
